import { ITemplateQuestionInfo, VIEW_MODE } from '../../../../interface/multiCapabilityTemplate';
import React, { Fragment, useState } from 'react';
import { lang } from '@umu/ukit-lang';
import { Iconfont, InputCountType, UmuInput } from '@umu/umu-design';
import { CapabilityProgressScore } from './capabilityScore';
import merge from 'lodash/merge';
import { ScoreRange } from '../scoreRange';
import { onCheckSetUp } from './dataFlow';
import { If } from 'common/src/components';

interface IProps {
    indexPrefix?: string | JSX.Element;
    indexSuffix?: string | JSX.Element;
    preCls?: string;
    isValid?: boolean;
    hideBorder?: boolean;
    info: ITemplateQuestionInfo;
    mode?: VIEW_MODE; // 编辑模式
    onChange?: (info: any) => void;
}
const { Textarea } = UmuInput;
const DESC_MAX_LENGTH = 500;
const CapabilityQuestionInfoView = (props: IProps) => {
    const { preCls = 'capability-question-info-view', indexSuffix, info, indexPrefix, hideBorder = false } = props;
    const list = info.list || [];
    return (
        <div className={`${preCls} ${hideBorder ? 'no-border' : 'border'}`}>
            {indexPrefix && <div className={`${preCls}-index`}>{indexPrefix}</div>}
            <div className={`${preCls}-content`}>
                {list.map((item, index) => {
                    return (
                        <Fragment key={`content-${index}`}>
                            <div className={`${preCls}-content-title`}>
                                <span>
                                    【{info.title}】{item.title || ''}
                                </span>
                                {indexSuffix}
                            </div>
                            <div className={`${preCls}-content-desc`} style={{ whiteSpace: 'pre-line' }}>{item.desc}</div>
                            <div className={`${preCls}-content-score`}>
                                <CapabilityProgressScore
                                    mode={VIEW_MODE.VIEW}
                                    minTag={item.extend?.setup?.number_min_tag || ''}
                                    maxTag={item.extend?.setup?.number_max_tag || ''}
                                    min={Number(item?.extend?.setup.number_min) || 0}
                                    max={Number(item?.extend?.setup.number_max) || 1}
                                />
                            </div>
                        </Fragment>
                    );
                })}
                <div className="info-desc-grey-label">
                    <Iconfont type="AddSmall" className="add-title-icon" />
                    {lang('添加文字反馈')}
                </div>
            </div>
        </div>
    );
};

const renderErrorLimit = (maxLength: number) => {
    return lang('请最多输入 ${maxLength} 个字').replace('${maxLength}', maxLength.toString());
};
const keyMap: Record<string, string> = {
    startValue: 'number_min',
    startLabel: 'number_min_tag',
    numberDefault: 'number_default',
    endValue: 'number_max',
    endLabel: 'number_max_tag',
};
const CapabilityQuestionInfoEditor = (props: IProps) => {
    const {
        indexPrefix,
        indexSuffix,
        preCls = 'capability-question-info-editor',
        info,
        isValid = false,
        hideBorder = false,
        onChange,
    } = props;
    const [dataInfo, setDataInfo] = useState(info);
    const onDataInfoChange = (data: any) => {
        const newInfo = {
            ...dataInfo,
            ...data,
        };
        setDataInfo(newInfo);
        onChange?.(newInfo);
    };
    const onQuestionChange = (index: number, option: any) => {
        const list = [...(dataInfo?.list || [])];
        list[index] = merge({}, list[index], option);
        onDataInfoChange({
            list,
        });
    };
    const onQuestionExtendChange = (index: number, key: string, value: string) => {
        const list = [...(dataInfo?.list || [])];
        const item: ITemplateQuestionInfo = list[index] || {};
        const extend: any = item?.extend || {};
        const setup: Record<string, string> = extend?.setup || {};
        setup[keyMap[key] as string] = value;
        const tmpSetup = onCheckSetUp(setup as any);
        list[index] = merge({}, item, { extend: { ...extend, setup: tmpSetup } });
        onDataInfoChange({
            list,
        });
    };
    return (
        <div className={`${preCls} ${hideBorder ? 'no-border' : 'border'}`}>
            <div className={`${preCls}-title`}>
                {indexPrefix && <span>{indexPrefix}</span>}
                <span>{dataInfo.title}</span>
                {indexSuffix && <span>{indexSuffix}</span>}
            </div>
            <div className={`${preCls}-content`}>
                <div className="is-require info-label">{lang('关联问题')}</div>

                {(dataInfo?.list || []).map((item, index) => {
                    const setup: any = item?.extend?.setup || {};
                    return (
                        <Fragment key={`content-${index}`}>
                            <Textarea
                                value={item.title}
                                onChange={(data) =>
                                    onQuestionChange(index, {
                                        title: data.value,
                                    })
                                }
                                isAllowEmpty={false}
                                maxLength={DESC_MAX_LENGTH}
                                errorLimit={renderErrorLimit}
                                countType={InputCountType.character}
                                expandConfig={{
                                    placeholder: lang('填写关联问题'),
                                    autoSize: {
                                        minRows: 1,
                                        maxRows: 5,
                                    },
                                }}
                            />
                            <If condition={isValid && item.title === ''}>
                                <div className="valid-text capability-input-error-option">{lang('填写关联问题')}</div>
                            </If>
                            <div className="is-require info-label">{lang('评价分值范围设置')}</div>
                            <div className="score-info">
                                <ScoreRange
                                    info={{
                                        startValue: setup?.[keyMap.startValue] || '',
                                        startLabel: setup?.[keyMap.startLabel] || '',
                                        endValue: setup?.[keyMap.endValue] || '',
                                        numberDefault: setup?.[keyMap.numberDefault] || '',
                                        endLabel: setup?.[keyMap.endLabel] || '',
                                    }}
                                    onChange={(key, value) => onQuestionExtendChange(index, key, value)}
                                />
                            </div>
                            <div className="info-label">{lang('描述（选填）')}</div>
                            <Textarea
                                value={item?.desc || ''}
                                onChange={(data) => onQuestionChange(index, { desc: data.value })}
                                isAllowEmpty={false}
                                maxLength={DESC_MAX_LENGTH}
                                errorLimit={renderErrorLimit}
                                countType={InputCountType.character}
                                expandConfig={{
                                    placeholder: lang('填写评价维度描述'),
                                    autoSize: {
                                        minRows: 3,
                                        maxRows: 5,
                                    },
                                }}
                            />
                        </Fragment>
                    );
                })}
                <div className="info-desc-label">
                    <Iconfont type="AddSmall" className="add-title-icon" />
                    {lang('添加文字反馈')}
                </div>
            </div>
        </div>
    );
};
const CapabilityQuestionInfo = (props: IProps) => {
    const isEditor = props.mode === VIEW_MODE.EDIT;
    const Element = isEditor ? CapabilityQuestionInfoEditor : CapabilityQuestionInfoView;
    return <Element {...props} />;
};
export { CapabilityQuestionInfo };
